import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { Flame, Plus, Pencil, Trash2, X, Check, AlertTriangle, ChevronUp, BarChart3, Loader2, Users, TrendingUp, Target, Zap } from "lucide-react"
import { useAuth, type AdminStreakEntry, type CreateStreakData, type StreakAnalytics } from "@/contexts/AuthContext"
import { AdminCard } from "./AdminCard"
import { StreakFormModal, emptyForm, type StreakFormData } from "./StreakForm"

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={["inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide font-admin", active ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20" : "bg-white/5 text-white/30 border border-white/10"].join(" ")}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-white/30"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  )
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number | string; icon: React.ElementType; color: string }) {
  return (
    <div className="rounded-lg border border-[#2A2A45] bg-[#0C0C12] p-3">
      <div className="flex items-center gap-2 mb-1"><Icon className="w-3.5 h-3.5" style={{ color }} /><span className="text-[10px] uppercase tracking-wider text-white/35 font-admin">{label}</span></div>
      <p className="text-lg font-semibold font-admin" style={{ color }}>{value}</p>
    </div>
  )
}

export function StreaksAdmin() {
  const { user, getAdminStreaks, createAdminStreak, updateAdminStreak, deleteAdminStreak, getAdminStreakAnalytics } = useAuth()
  const [streaks, setStreaks] = React.useState<AdminStreakEntry[]>([])
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(null)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [form, setForm] = React.useState<StreakFormData>(emptyForm())
  const [feedback, setFeedback] = React.useState<{ type: "success" | "error"; text: string } | null>(null)
  const [expandedAnalytics, setExpandedAnalytics] = React.useState<string | null>(null)
  const [analytics, setAnalytics] = React.useState<Record<string, StreakAnalytics>>({})
  const [loadingAnalytics, setLoadingAnalytics] = React.useState<string | null>(null)

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "support"

  const loadStreaks = React.useCallback(async () => {
    setLoading(true)
    const result = await getAdminStreaks()
    if (result.success && result.streaks) setStreaks(result.streaks)
    else setFeedback({ type: "error", text: result.message || "Failed to load streaks" })
    setLoading(false)
  }, [getAdminStreaks])

  React.useEffect(() => { loadStreaks() }, [loadStreaks])
  React.useEffect(() => { if (!feedback) return; const t = setTimeout(() => setFeedback(null), 4000); return () => clearTimeout(t) }, [feedback])

  const openCreateModal = () => { setEditingId(null); setForm(emptyForm()); setModalOpen(true) }
  const openEditModal = (streak: AdminStreakEntry) => {
    setEditingId(streak._id)
    setForm({ name: streak.name, type: streak.type as any, description: streak.description, milestones: [], defaultReward: { type: "points", amount: 25 }, resetOnMiss: true, gracePeriodDays: 0, maxStreak: null, isActive: streak.isActive, startDate: "", endDate: "" })
    setModalOpen(true)
  }
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(emptyForm()) }
  const updateForm = <K extends keyof StreakFormData>(key: K, value: StreakFormData[K]) => { setForm((prev) => ({ ...prev, [key]: value })) }

  const handleSave = async () => {
    if (!form.name.trim()) { setFeedback({ type: "error", text: "Name is required" }); return }
    setSaving(true)
    const data: CreateStreakData = { name: form.name.trim(), type: form.type, description: form.description.trim(), milestones: form.milestones, defaultReward: form.defaultReward, resetOnMiss: form.resetOnMiss, gracePeriodDays: form.gracePeriodDays, maxStreak: form.maxStreak || undefined, isActive: form.isActive, startDate: form.startDate || undefined, endDate: form.endDate || undefined }
    const result = editingId ? await updateAdminStreak(editingId, data) : await createAdminStreak(data)
    if (result.success) { setFeedback({ type: "success", text: editingId ? "Streak updated" : "Streak created" }); closeModal(); loadStreaks() }
    else setFeedback({ type: "error", text: result.message || "Failed to save" })
    setSaving(false)
  }

  const handleDelete = async (id: string) => {
    const result = await deleteAdminStreak(id)
    if (result.success) { setFeedback({ type: "success", text: "Streak deleted" }); setDeleteConfirmId(null); loadStreaks() }
    else setFeedback({ type: "error", text: result.message || "Failed to delete" })
  }

  const toggleAnalytics = async (id: string) => {
    if (expandedAnalytics === id) { setExpandedAnalytics(null); return }
    setExpandedAnalytics(id)
    if (!analytics[id]) {
      setLoadingAnalytics(id)
      const result = await getAdminStreakAnalytics(id)
      if (result.success && result.analytics) setAnalytics((prev) => ({ ...prev, [id]: result.analytics! }))
      setLoadingAnalytics(null)
    }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" /></div>

  return (
    <div className="space-y-6">
      {/* Feedback Toast */}
      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ opacity: 0, y: -20, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -20, x: "-50%" }} className="fixed top-4 left-1/2 z-[60]">
            <div className={["flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium font-admin shadow-2xl backdrop-blur-sm", feedback.type === "success" ? "bg-emerald-500/15 border-emerald-500/25 text-emerald-400" : "bg-red-500/15 border-red-500/25 text-red-400"].join(" ")}>
              {feedback.type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{feedback.text}
              <button type="button" onClick={() => setFeedback(null)} className="ml-2 opacity-50 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#F37020]/20 to-[#F37020]/5 border border-[#F37020]/20 flex items-center justify-center"><Flame className="w-5 h-5 text-[#F37020]" /></div>
          <div><h2 className="text-lg font-semibold text-white/90 font-admin">Streaks</h2><p className="text-xs text-white/30 font-admin">{streaks.length} streak{streaks.length !== 1 ? "s" : ""} configured</p></div>
        </div>
        {isSuperAdmin && <button type="button" onClick={openCreateModal} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#F37020] hover:bg-[#F37020]/90 text-white text-sm font-medium font-admin transition-colors"><Plus className="w-4 h-4" />Create Streak</button>}
      </motion.div>

      {/* Streaks List */}
      {streaks.length === 0 ? (
        <AdminCard delay={0.1}>
          <div className="text-center py-12"><Flame className="w-10 h-10 text-white/10 mx-auto mb-3" /><p className="text-sm text-white/40 font-admin">No streaks configured yet</p>
            {isSuperAdmin && <button type="button" onClick={openCreateModal} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F37020]/15 border border-[#F37020]/25 text-sm text-[#F37020] hover:bg-[#F37020]/25 transition-colors font-admin"><Plus className="w-4 h-4" />Create your first streak</button>}
          </div>
        </AdminCard>
      ) : (
        <div className="space-y-3">
          {streaks.map((streak, i) => (
            <motion.div key={streak._id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-[#111117] border border-[#2A2A45] rounded-xl overflow-hidden">
              <div className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#F37020]/10 border border-[#F37020]/20 flex items-center justify-center flex-shrink-0"><Flame className="w-5 h-5 text-[#F37020]" /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2"><h3 className="text-sm font-semibold text-white/90 font-admin truncate">{streak.name}</h3><StatusBadge active={streak.isActive} /></div>
                      <div className="flex items-center gap-3 mt-1"><span className="text-[11px] text-white/40 font-admin capitalize">{streak.type.replace(/_/g, " ")}</span><span className="text-[11px] text-white/25">•</span><span className="text-[11px] text-white/40 font-admin">{streak.milestonesCount} milestones</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right hidden sm:block"><p className="text-sm font-semibold text-[#F37020] font-mono-admin">{streak.activeUsers}</p><p className="text-[10px] text-white/30 font-admin">Active Users</p></div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => toggleAnalytics(streak._id)} className="w-8 h-8 rounded-lg bg-white/[0.04] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.08] transition-colors" title="Analytics">{expandedAnalytics === streak._id ? <ChevronUp className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}</button>
                      {isSuperAdmin && (<>
                        <button type="button" onClick={() => openEditModal(streak)} className="w-8 h-8 rounded-lg bg-white/[0.04] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.08] transition-colors" title="Edit"><Pencil className="w-4 h-4" /></button>
                        <button type="button" onClick={() => setDeleteConfirmId(streak._id)} className="w-8 h-8 rounded-lg bg-white/[0.04] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </>)}
                    </div>
                  </div>
                </div>
                {streak.description && <p className="mt-3 text-xs text-white/35 font-admin pl-14">{streak.description}</p>}
              </div>
              <AnimatePresence>
                {expandedAnalytics === streak._id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="border-t border-[#2A2A45] overflow-hidden">
                    <div className="p-4 bg-[#0C0C12]">
                      {loadingAnalytics === streak._id ? <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-[#F37020] animate-spin" /></div>
                        : analytics[streak._id] ? (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <StatCard label="Total Users" value={analytics[streak._id].totalUsers} icon={Users} color="#F37020" />
                            <StatCard label="Active Users" value={analytics[streak._id].activeUsers} icon={Zap} color="#22c55e" />
                            <StatCard label="Avg Streak" value={`${analytics[streak._id].avgStreakLength.toFixed(1)} days`} icon={TrendingUp} color="#3b82f6" />
                            <StatCard label="Completion" value={`${analytics[streak._id].completionRate.toFixed(0)}%`} icon={Target} color="#eab308" />
                          </div>
                        ) : <p className="text-center text-sm text-white/30 font-admin py-4">No analytics data</p>}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirmId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setDeleteConfirmId(null)}>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-[#111117] border border-[#2A2A45] rounded-xl p-6 max-w-sm mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-4"><div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-5 h-5 text-red-400" /></div><div><h3 className="text-base font-semibold text-white font-admin">Delete Streak?</h3><p className="text-sm text-white/50 font-admin mt-1">This will permanently delete the streak and all user progress.</p></div></div>
              <div className="flex items-center gap-3 mt-6">
                <button type="button" onClick={() => setDeleteConfirmId(null)} className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] border border-[#2A2A45] text-sm text-white/70 font-admin hover:bg-white/[0.08] transition-colors">Cancel</button>
                <button type="button" onClick={() => handleDelete(deleteConfirmId)} className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/15 border border-red-500/25 text-sm text-red-400 font-admin hover:bg-red-500/25 transition-colors">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
            <StreakFormModal form={form} editingId={editingId} saving={saving} onClose={closeModal} onSave={handleSave} updateForm={updateForm} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
